import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import Header from "../components/Header";

// Maps a percentage (0–100) to a letter grade
function calcLetterGrade(pct) {
  if (pct >= 90) return "A";
  if (pct >= 80) return "B";
  if (pct >= 70) return "C";
  if (pct >= 60) return "D";
  return "F";
}

export default function Quiz() {
  const { id, quizId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // editingRevision tracks whether the child is actively re-editing a kicked-back quiz
  const [editingRevision, setEditingRevision] = useState(false);
  // Ref mirrors editingRevision so the onSnapshot callback can read the current
  // value without stale closure issues — guards setAnswers from wiping in-progress edits.
  const editingRevisionRef = React.useRef(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "books", id), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setBook({ id: snap.id, ...data });
        const found = (data.quizzes || []).find((q) => q.id === quizId);
        setQuiz(found || null);
        // Only sync answers from Firestore when NOT actively revising —
        // otherwise onSnapshot would overwrite the student's in-progress edits
        if (!editingRevisionRef.current) {
          setAnswers(found?.childAnswers || {});
        }
      }
    });
    return unsubscribe;
  }, [id, quizId]);

  // When the student opens a quiz that's been reviewed, mark the feedback
  // as seen so the alert badge clears. Runs only when the reviewed status
  // or seen flag changes — not on every unrelated book update.
  useEffect(() => {
    if (!quiz || !book) return;
    if (quiz.reviewedByParent && quiz.feedbackSeenByChild !== true) {
      const updatedQuizzes = (book.quizzes || []).map((q) =>
        q.id === quizId ? { ...q, feedbackSeenByChild: true } : q
      );
      updateDoc(doc(db, "books", id), { quizzes: updatedQuizzes }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz?.id, quiz?.reviewedByParent, quiz?.feedbackSeenByChild]);

  function handleSingleAnswer(questionIndex, value) {
    setAnswers((prev) => ({ ...prev, [questionIndex]: value }));
  }

  function handleMultiSelect(questionIndex, optionIndex) {
    setAnswers((prev) => {
      const current = prev[questionIndex] || [];
      if (current.includes(optionIndex)) {
        return { ...prev, [questionIndex]: current.filter((i) => i !== optionIndex) };
      } else {
        return { ...prev, [questionIndex]: [...current, optionIndex] };
      }
    });
  }

  // Lets the child start editing their answers again after a kick-back
  function startRevision() {
    editingRevisionRef.current = true;
    setEditingRevision(true);
  }

  async function submitQuiz() {
    if (!quiz) return;

    // Check all questions are answered
    const unanswered = quiz.questions.filter((q, i) => {
      if (q.type === "multi_select") return !answers[i] || answers[i].length === 0;
      return answers[i] === undefined || answers[i] === "";
    });
    if (unanswered.length > 0) {
      return alert(`Please answer all questions before submitting. You have ${unanswered.length} unanswered.`);
    }

    setSubmitting(true);
    try {
      const updatedQuizzes = (book.quizzes || []).map((q) => {
        if (q.id !== quizId) return q;
        // Resubmitting clears needsRevision AND reviewedByParent — new answers
        // mean the parent must look at this again. completedByChild stays true throughout.
        return { ...q, completedByChild: true, childAnswers: answers, needsRevision: false, reviewedByParent: false };
      });
      await updateDoc(doc(db, "books", id), { quizzes: updatedQuizzes });
      // Clear revision mode — onSnapshot will update quiz.completedByChild
      // and quiz.needsRevision which drives isLockedView from here on
      editingRevisionRef.current = false;
      setEditingRevision(false);
    } catch {
      alert("Failed to submit quiz. Try again.");
    }
    setSubmitting(false);
  }

  function calcAutoScore() {
    if (!quiz) return { earned: 0, possible: 0 };
    let earned = 0;
    let possible = 0;
    quiz.questions.forEach((q, i) => {
      if (q.type === "free_response") return;
      if (q.type === "multi_select") {
        const correct = q.correctAnswers || [];
        const selected = answers[i] || [];
        possible += correct.length;
        correct.forEach((ci) => { if (selected.includes(ci)) earned++; });
      } else {
        possible += 1;
        if (answers[i] === q.correctAnswer) earned++;
      }
    });
    return { earned, possible };
  }

  function calcTotalScore() {
    if (!quiz) return { earned: 0, possible: 0 };
    const auto = calcAutoScore();
    let freeEarned = 0;
    let freePossible = 0;
    quiz.questions.forEach((q, i) => {
      if (q.type !== "free_response") return;
      freePossible += 10;
      if (quiz.parentScores && quiz.parentScores[i] !== undefined) {
        freeEarned += quiz.parentScores[i];
      }
    });
    return {
      earned: auto.earned + freeEarned,
      possible: auto.possible + freePossible,
      freeFullyScored: Object.keys(quiz.parentScores || {}).length >=
        quiz.questions.filter((q) => q.type === "free_response").length
    };
  }

  if (!book || !quiz) return <div style={styles.loading}>Loading...</div>;

  if (!quiz.unlockedByParent) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={styles.lockedContainer}>
          <p style={styles.lockedIcon}>🔒</p>
          <h2 style={styles.lockedTitle}>Quiz Locked</h2>
          <p style={styles.lockedText}>This quiz hasn't been unlocked by your parent yet. Check back soon!</p>
          <button style={styles.backBtn} onClick={() => navigate(`/book/${id}`)}>← Back to Book</button>
        </div>
      </div>
    );
  }

  const totalScore = calcTotalScore();
  const percentage = totalScore.possible > 0
    ? Math.round((totalScore.earned / totalScore.possible) * 100)
    : 0;
  const letterGrade = calcLetterGrade(percentage);
  // Gate the "Final Score" display on the parent explicitly finalizing the review,
  // not on auto-detecting whether free-response questions are scored. Without this,
  // quizzes with zero free-response questions would show "Final Score" the instant
  // the child submits, before the parent has reviewed anything.
  const isReviewed = !!quiz.reviewedByParent;

  // Locked = quiz has been submitted AND the child isn't actively revising.
  // Derived from Firestore data (quiz.completedByChild) rather than local state
  // so onSnapshot can never accidentally re-lock the quiz mid-revision.
  const isLockedView = !!quiz?.completedByChild && !editingRevision;

  return (
    <div style={styles.container}>
      <Header />

      <div style={styles.pageHeader}>
        <button style={styles.headerBackBtn} onClick={() => navigate(`/book/${id}`)}>← Back</button>
        <div style={styles.headerText}>
          <h2 style={styles.title}>{quiz.title}</h2>
          <p style={styles.subtitle}>{book.title}</p>
        </div>
      </div>

      <div style={styles.content}>

        {/* Revision needed banner */}
        {quiz.needsRevision && !editingRevision && (
          <div style={styles.revisionBanner}>
            <p style={styles.revisionTitle}>🔁 Your parent asked you to take another look at this quiz!</p>
            <p style={styles.revisionText}>
              Check the feedback below each question, then tap the button to update your answers.
            </p>
            <button style={styles.revisionBtn} onClick={startRevision}>
              ✏️ Revise My Answers
            </button>
          </div>
        )}

        {/* Score banner if submitted and not currently revising */}
        {isLockedView && (
          <div style={{
            ...styles.scoreBanner,
            backgroundColor: isReviewed
              ? (letterGrade === "A" ? "#2f855a" : letterGrade === "B" ? "#2b6cb0" : letterGrade === "C" ? "#b7791f" : "#c53030")
              : "#2f855a"
          }}>
            {isReviewed ? (
              <>
                <p style={styles.scoreTitle}>✅ Final Score</p>
                <p style={styles.scoreNumber}>{totalScore.earned} / {totalScore.possible}</p>
                <p style={styles.scorePercent}>{percentage}%</p>
                <div style={styles.gradeBadge}>
                  <span style={styles.gradeLabel}>{letterGrade}</span>
                </div>
              </>
            ) : (
              <>
                <p style={styles.scoreTitle}>✅ Quiz Submitted</p>
                <p style={styles.scoreSubtext}>Your parent still needs to review this quiz.</p>
                <p style={styles.scorePartial}>
                  Auto-scored so far: {calcAutoScore().earned} / {calcAutoScore().possible} pts
                </p>
              </>
            )}
          </div>
        )}

        {/* Questions */}
        {quiz.questions.map((q, i) => (
          <div key={i} style={styles.card}>
            <p style={styles.questionNumber}>Question {i + 1}</p>
            <p style={styles.questionText}>{q.question}</p>

            {/* Multiple Choice */}
            {q.type === "multiple_choice" && (
              <div style={styles.optionsList}>
                {q.options.map((opt, oi) => {
                  const selected = answers[i] === oi;
                  const showCorrect = isLockedView && oi === q.correctAnswer;
                  const showWrong = isLockedView && selected && oi !== q.correctAnswer;
                  return (
                    <button
                      key={oi}
                      style={{
                        ...styles.optionBtn,
                        backgroundColor: showCorrect ? "#c6f6d5" : showWrong ? "#fed7d7" : selected ? "#ebf8ff" : "white",
                        borderColor: showCorrect ? "#48bb78" : showWrong ? "#f56565" : selected ? "#4299e1" : "#e2e8f0",
                      }}
                      onClick={() => !isLockedView && handleSingleAnswer(i, oi)}
                    >
                      <span style={styles.optionLetter}>{String.fromCharCode(65 + oi)}.</span>
                      {opt}
                      {showCorrect && <span style={styles.correctTag}> ✓</span>}
                      {showWrong && <span style={styles.wrongTag}> ✗</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* True / False */}
            {q.type === "true_false" && (
              <div style={styles.tfRow}>
                {["True", "False"].map((label, oi) => {
                  const selected = answers[i] === oi;
                  const showCorrect = isLockedView && oi === q.correctAnswer;
                  const showWrong = isLockedView && selected && oi !== q.correctAnswer;
                  return (
                    <button
                      key={oi}
                      style={{
                        ...styles.tfBtn,
                        backgroundColor: showCorrect ? "#c6f6d5" : showWrong ? "#fed7d7" : selected ? "#ebf8ff" : "white",
                        borderColor: showCorrect ? "#48bb78" : showWrong ? "#f56565" : selected ? "#4299e1" : "#e2e8f0",
                      }}
                      onClick={() => !isLockedView && handleSingleAnswer(i, oi)}
                    >
                      {label}
                      {showCorrect && " ✓"}
                      {showWrong && " ✗"}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Multi Select */}
            {q.type === "multi_select" && (
              <div>
                <p style={styles.multiHint}>
                  Select <strong><u>ALL</u></strong> that apply
                </p>
                <div style={styles.optionsList}>
                  {q.options.map((opt, oi) => {
                    const selected = (answers[i] || []).includes(oi);
                    const showCorrect = isLockedView && (q.correctAnswers || []).includes(oi);
                    const showWrong = isLockedView && selected && !(q.correctAnswers || []).includes(oi);
                    return (
                      <button
                        key={oi}
                        style={{
                          ...styles.optionBtn,
                          backgroundColor: showCorrect ? "#c6f6d5" : showWrong ? "#fed7d7" : selected ? "#ebf8ff" : "white",
                          borderColor: showCorrect ? "#48bb78" : showWrong ? "#f56565" : selected ? "#4299e1" : "#e2e8f0",
                        }}
                        onClick={() => !isLockedView && handleMultiSelect(i, oi)}
                      >
                        <span style={styles.optionLetter}>{selected ? "☑" : "☐"}</span>
                        {opt}
                        {showCorrect && <span style={styles.correctTag}> ✓</span>}
                        {showWrong && <span style={styles.wrongTag}> ✗</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Dropdown */}
            {q.type === "dropdown" && (
              <select
                style={styles.dropdown}
                value={answers[i] !== undefined ? answers[i] : ""}
                onChange={(e) => !isLockedView && handleSingleAnswer(i, parseInt(e.target.value))}
                disabled={isLockedView}
              >
                <option value="">-- Select an answer --</option>
                {q.options.map((opt, oi) => (
                  <option key={oi} value={oi}>{opt}</option>
                ))}
              </select>
            )}
            {isLockedView && q.type === "dropdown" && (
              <p style={{
                ...styles.dropdownResult,
                color: answers[i] === q.correctAnswer ? "#2f855a" : "#c53030"
              }}>
                {answers[i] === q.correctAnswer ? "✓ Correct" : `✗ Correct answer: ${q.options[q.correctAnswer]}`}
              </p>
            )}

            {/* Free Response */}
            {q.type === "free_response" && (
              <div>
                <textarea
                  style={styles.textarea}
                  placeholder="Write your answer here..."
                  value={answers[i] || ""}
                  onChange={(e) => !isLockedView && handleSingleAnswer(i, e.target.value)}
                  rows={4}
                  disabled={isLockedView}
                />
                {isLockedView && (
                  <div style={styles.freeScoreBox}>
                    {quiz.parentScores && quiz.parentScores[i] !== undefined ? (
                      <p style={styles.freeScoreText}>Parent score: <strong>{quiz.parentScores[i]} / 10</strong></p>
                    ) : (
                      <p style={styles.freeScorePending}>⏳ Awaiting parent score</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Parent feedback — only shown once parent has finalized OR sent back
                for revision. A "Save Progress" draft stays private until then. */}
            {(isReviewed || quiz.needsRevision) && q.parentFeedback && q.parentFeedback.trim() !== "" && (
              <div style={styles.feedbackBox}>
                <p style={styles.feedbackLabel}>💬 Feedback from your parent:</p>
                <p style={styles.feedbackText}>{q.parentFeedback}</p>
              </div>
            )}
          </div>
        ))}

        {/* Submit button — shown when not yet submitted, or actively revising */}
        {!isLockedView && (
          <button style={styles.submitBtn} onClick={submitQuiz} disabled={submitting}>
            {submitting ? "Submitting..." : editingRevision ? "Resubmit Quiz" : "Submit Quiz"}
          </button>
        )}

      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" },

  lockedContainer: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: 24, textAlign: "center" },
  lockedIcon: { fontSize: 48, margin: "0 0 16px 0" },
  lockedTitle: { fontSize: 24, color: "#2d3748", margin: "0 0 8px 0" },
  lockedText: { fontSize: 16, color: "#718096", margin: "0 0 24px 0", maxWidth: 320 },
  backBtn: { padding: "12px 24px", backgroundColor: "#e2e8f0", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer" },

  pageHeader: { backgroundColor: "white", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" },
  headerBackBtn: { padding: "8px 14px", backgroundColor: "#e2e8f0", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, whiteSpace: "nowrap" },
  headerText: { flex: 1 },
  title: { fontSize: 18, color: "#2d3748", margin: "0 0 2px 0" },
  subtitle: { fontSize: 13, color: "#718096", margin: 0 },

  content: { padding: 20, maxWidth: 700, margin: "0 auto" },

  revisionBanner: { backgroundColor: "#fff5f5", border: "2px solid #fc8181", borderRadius: 12, padding: "18px 20px", marginBottom: 20, textAlign: "center" },
  revisionTitle: { fontSize: 16, fontWeight: "bold", color: "#c53030", margin: "0 0 8px 0" },
  revisionText: { fontSize: 14, color: "#742a2a", margin: "0 0 14px 0", lineHeight: 1.5 },
  revisionBtn: { padding: "12px 24px", backgroundColor: "#c53030", color: "white", border: "none", borderRadius: 8, fontSize: 15, fontWeight: "bold", cursor: "pointer" },

  scoreBanner: { borderRadius: 12, padding: "20px 24px", marginBottom: 20, textAlign: "center" },
  scoreTitle: { color: "white", fontSize: 16, fontWeight: "bold", margin: "0 0 8px 0" },
  scoreNumber: { color: "white", fontSize: 36, fontWeight: "bold", margin: "0 0 4px 0" },
  scorePercent: { color: "rgba(255,255,255,0.85)", fontSize: 18, margin: "0 0 12px 0" },
  gradeBadge: { display: "inline-block", backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 12, padding: "6px 24px", border: "2px solid rgba(255,255,255,0.5)" },
  gradeLabel: { color: "white", fontSize: 32, fontWeight: "bold", lineHeight: 1 },
  scoreSubtext: { color: "white", fontSize: 14, margin: "0 0 8px 0", opacity: 0.9 },
  scorePartial: { color: "rgba(255,255,255,0.8)", fontSize: 13, margin: 0 },

  card: { backgroundColor: "white", borderRadius: 12, padding: 24, marginBottom: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" },
  questionNumber: { fontSize: 12, color: "#a0aec0", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 6px 0" },
  questionText: { fontSize: 17, color: "#2d3748", fontWeight: "bold", margin: "0 0 16px 0", lineHeight: 1.5 },

  optionsList: { display: "flex", flexDirection: "column", gap: 10 },
  optionBtn: { display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 8, border: "2px solid", cursor: "pointer", fontSize: 15, color: "#2d3748", textAlign: "left", backgroundColor: "white", transition: "all 0.15s ease" },
  optionLetter: { fontWeight: "bold", color: "#718096", minWidth: 20 },
  correctTag: { marginLeft: "auto", color: "#2f855a", fontWeight: "bold" },
  wrongTag: { marginLeft: "auto", color: "#c53030", fontWeight: "bold" },

  tfRow: { display: "flex", gap: 12 },
  tfBtn: { flex: 1, padding: "14px 16px", borderRadius: 8, border: "2px solid", cursor: "pointer", fontSize: 16, fontWeight: "bold", color: "#2d3748", backgroundColor: "white" },

  multiHint: { fontSize: 13, color: "#718096", margin: "0 0 10px 0" },

  dropdown: { width: "100%", padding: "12px 16px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 15, color: "#2d3748", backgroundColor: "white", marginBottom: 8 },
  dropdownResult: { fontSize: 14, fontWeight: "bold", margin: "4px 0 0 0" },

  textarea: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 15, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" },
  freeScoreBox: { marginTop: 10, padding: "10px 14px", backgroundColor: "#f7fafc", borderRadius: 8 },
  freeScoreText: { fontSize: 14, color: "#2d3748", margin: 0 },
  freeScorePending: { fontSize: 14, color: "#a0aec0", margin: 0 },

  feedbackBox: { marginTop: 12, padding: "12px 14px", backgroundColor: "#ebf8ff", borderRadius: 8, borderLeft: "3px solid #4299e1" },
  feedbackLabel: { fontSize: 12, color: "#2b6cb0", fontWeight: "bold", margin: "0 0 4px 0", textTransform: "uppercase" },
  feedbackText: { fontSize: 14, color: "#2d3748", margin: 0, lineHeight: 1.5 },

  submitBtn: { width: "100%", padding: 16, backgroundColor: "#4299e1", color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer", fontWeight: "bold", marginBottom: 24 },
};